/**
 * PIELearnerTrendCard — trailing-12-week 3-line chart of the
 * learner's own pulse signals (Confidence, Application, Impact)
 * + a friction-tag breakdown bar.
 *
 * Renders inline on the Dashboard. Uses pure SVG (no chart library)
 * so the bundle stays tiny. Hides itself when no pulses exist yet
 * (first-week users) so the dashboard isn't cluttered with empty
 * charts.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertTriangle, ArrowRight } from 'lucide-react';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';
import { usePIEDisplayMode, valueToBucket } from './PIEDisplayMode';

const SERIES = [
  { key: 'confidence', label: 'Confidence', color: 'var(--pie-accent)' },
  { key: 'application', label: 'Application', color: '#7C3AED' },
  { key: 'impact', label: 'Impact', color: '#F59E0B' },
];

const TAG_LABELS = {
  time: 'Time',
  clarity: 'Clarity',
  tools: 'Tools',
  support: 'Support',
};

export default function PIELearnerTrendCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    pieApi.trendsLearner(12)
      .then(setData)
      .catch((e) => {
        logger.warn('trendsLearner failed', e);
        setErr(true);
      });
  }, []);

  if (err || !data) return null;
  if (!data.total_samples) return null; // hide for fresh accounts

  return (
    <section
      className="pie-card"
      style={{ marginBottom: 24, padding: '20px 22px' }}
      data-testid="pie-learner-trend-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <h2 className="pie-display-md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrendingUp size={16} aria-hidden style={{ color: 'var(--pie-accent)' }} />
          Your last {data.weeks} weeks
        </h2>
        <Link
          to="/mirror/pulses"
          className="pie-link"
          data-testid="pie-learner-trend-history-link"
          style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          See pulse history <ArrowRight size={11} />
        </Link>
      </div>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 12, lineHeight: 1.55 }}>
        Three signals from your weekly pulse, side-by-side. Wide gaps between them
        are usually the most useful pattern (e.g. high confidence but low application
        means something is blocking you, not your readiness).
      </p>

      <Legend />
      <TrendChart series={data.series} scaleMax={data.scale_max} latest={data.latest} />
      <FrictionBar breakdown={data.friction_breakdown} totalSamples={data.total_samples} />
    </section>
  );
}

function Legend() {
  return (
    <div
      style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}
      data-testid="pie-learner-trend-legend"
    >
      {SERIES.map((s) => (
        <div
          key={s.key}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--pie-text-secondary)' }}
          data-testid={`pie-learner-trend-legend-${s.key}`}
        >
          <span
            aria-hidden
            style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, display: 'inline-block' }}
          />
          {s.label}
        </div>
      ))}
    </div>
  );
}

function TrendChart({ series, scaleMax, latest }) {
  const { mode, thresholds } = usePIEDisplayMode();
  const W = 720;
  const H = 220;
  const PAD_L = 28;
  const PAD_R = 16;
  const PAD_T = 12;
  const PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xFor = (i) => PAD_L + (series.length === 1 ? 0 : (i / (series.length - 1)) * innerW);
  const yFor = (val) => {
    // val is 1..scaleMax. Map 1 → bottom, scaleMax → top.
    const norm = (val - 1) / (scaleMax - 1);
    return PAD_T + innerH * (1 - norm);
  };

  // Horizontal gridlines at 1,2,3,4,5
  const gridY = [];
  for (let v = 1; v <= scaleMax; v += 1) {
    gridY.push({ v, y: yFor(v) });
  }

  const pathFor = (key) => {
    let d = '';
    let started = false;
    series.forEach((pt, i) => {
      const v = pt[key];
      if (v == null) {
        started = false;
        return;
      }
      const cmd = started ? 'L' : 'M';
      d += `${cmd}${xFor(i).toFixed(1)},${yFor(v).toFixed(1)} `;
      started = true;
    });
    return d.trim();
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      role="img"
      aria-label="Trend chart of confidence, application and impact across weeks"
      data-testid="pie-learner-trend-svg"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Gridlines.
          In verbiage mode the numeric labels (1..5) are suppressed so
          the chart reads as a shape, not a score. The horizontal lines
          stay because they're a useful visual scaffold. */}
      {gridY.map((g) => (
        <g key={g.v}>
          <line
            x1={PAD_L} y1={g.y} x2={W - PAD_R} y2={g.y}
            stroke="var(--pie-border)" strokeWidth="1" strokeDasharray="2 4"
          />
          {mode !== 'verbiage' && (
            <text
              x={PAD_L - 6} y={g.y + 3}
              fontSize="10" fill="var(--pie-text-muted)" textAnchor="end"
            >
              {g.v}
            </text>
          )}
        </g>
      ))}

      {/* Week labels (sparse — every 3rd week) */}
      {series.map((pt, i) => {
        if (i % 3 !== 0 && i !== series.length - 1) return null;
        const wk = pt.week_iso?.split('-W')[1] || '';
        return (
          <text
            key={pt.week_iso}
            x={xFor(i)} y={H - 8}
            fontSize="10" fill="var(--pie-text-muted)" textAnchor="middle"
          >
            W{wk}
          </text>
        );
      })}

      {/* Lines */}
      {SERIES.map((s) => (
        <path
          key={s.key}
          d={pathFor(s.key)}
          fill="none"
          stroke={s.color}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          data-testid={`pie-learner-trend-line-${s.key}`}
        />
      ))}

      {/* Dots on every actual data point */}
      {SERIES.flatMap((s) =>
        series.map((pt, i) => {
          const v = pt[s.key];
          if (v == null) return null;
          return (
            <circle
              key={`${s.key}-${pt.week_iso}`}
              cx={xFor(i)}
              cy={yFor(v)}
              r="3"
              fill={s.color}
              stroke="var(--pie-card)"
              strokeWidth="1.5"
            />
          );
        })
      )}

      {/* "Now" label on the rightmost real value per series.
          In verbiage mode we replace the raw number with the tier
          label (e.g. "On Track") sourced from the org's thresholds. */}
      {SERIES.map((s) => {
        const v = latest?.[s.key];
        if (v == null) return null;
        const lastIdx = (() => {
          for (let i = series.length - 1; i >= 0; i -= 1) {
            if (series[i][s.key] != null) return i;
          }
          return -1;
        })();
        if (lastIdx < 0) return null;
        const label = mode === 'verbiage'
          ? valueToBucket(v, thresholds, 'pulse5') || ''
          : v.toFixed(1);
        return (
          <text
            key={`${s.key}-now`}
            x={xFor(lastIdx) + 8}
            y={yFor(v) + 3}
            fontSize={mode === 'verbiage' ? 9 : 10}
            fill={s.color}
            fontWeight="600"
            data-testid={`pie-learner-trend-now-${s.key}`}
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

function FrictionBar({ breakdown, totalSamples }) {
  const tags = Object.keys(TAG_LABELS);
  const total = tags.reduce((s, t) => s + (breakdown?.[t] || 0), 0);
  if (!total) {
    return (
      <div
        style={{
          marginTop: 14, padding: '10px 12px', borderRadius: 8,
          background: 'var(--pie-surface)', fontSize: 12, color: 'var(--pie-text-muted)',
        }}
        data-testid="pie-learner-friction-empty"
      >
        No friction tags raised in the last {totalSamples} pulses — keep it going.
      </div>
    );
  }
  const max = Math.max(...tags.map((t) => breakdown?.[t] || 0)) || 1;
  return (
    <div
      style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--pie-border)' }}
      data-testid="pie-learner-friction-bar"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <AlertTriangle size={13} aria-hidden style={{ color: '#B45309' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pie-text)' }}>
          What got in the way
        </div>
        <div style={{ fontSize: 11, color: 'var(--pie-text-muted)' }}>
          (across last {totalSamples} pulses)
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        {tags.map((t) => {
          const count = breakdown?.[t] || 0;
          const pctOfMax = (count / max) * 100;
          return (
            <div key={t} data-testid={`pie-learner-friction-${t}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                <span style={{ color: 'var(--pie-text-secondary)' }}>{TAG_LABELS[t]}</span>
                <span className="pie-mono" style={{ color: 'var(--pie-text)', fontWeight: 600 }}>{count}</span>
              </div>
              <div style={{ height: 6, background: 'var(--pie-surface-2)', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${pctOfMax}%`, height: '100%',
                    background: count === 0 ? 'transparent' : 'linear-gradient(90deg, #F59E0B, #DC2626)',
                    transition: 'width 0.4s ease',
                  }}
                  aria-hidden
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
